import { useEffect, useState } from 'react'

/** Desktop shell from lg (1024px) up — unless installed PWA / home screen. */
export const APP_SHELL_DESKTOP_MQ = '(min-width: 1024px)'

const STANDALONE_MQS = [
  '(display-mode: standalone)',
  '(display-mode: fullscreen)',
  '(display-mode: minimal-ui)',
] as const

export function isPwaDisplayMode(): boolean {
  if (typeof window === 'undefined') return false
  if (STANDALONE_MQS.some((q) => window.matchMedia(q).matches)) return true
  // iOS Safari "Add to Home Screen" (older + current)
  return (window.navigator as Navigator & { standalone?: boolean }).standalone === true
}

export function getAppShellMode(): 'mobile' | 'desktop' {
  if (typeof window === 'undefined') return 'mobile'
  if (isPwaDisplayMode()) return 'mobile'
  if (window.matchMedia(APP_SHELL_DESKTOP_MQ).matches) return 'desktop'
  return 'mobile'
}

/**
 * Field app chrome: phone widths and installed PWAs (Android + iOS) use the
 * mobile shell (bottom nav). Wide browser windows use the desktop sidebar.
 * Uses matchMedia only — not user-agent sniffing.
 */
export function useAppShellMode(): 'mobile' | 'desktop' {
  const [mode, setMode] = useState<'mobile' | 'desktop'>(() => getAppShellMode())

  useEffect(() => {
    const update = () => {
      const next = getAppShellMode()
      setMode(next)
      document.documentElement.classList.toggle('pwa-standalone', isPwaDisplayMode())
    }
    const widthMq = window.matchMedia(APP_SHELL_DESKTOP_MQ)
    const standMqs = STANDALONE_MQS.map((q) => window.matchMedia(q))

    widthMq.addEventListener('change', update)
    standMqs.forEach((mq) => mq.addEventListener('change', update))
    update()

    return () => {
      widthMq.removeEventListener('change', update)
      standMqs.forEach((mq) => mq.removeEventListener('change', update))
    }
  }, [])

  return mode
}
